import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, BarChart3Icon, CompassIcon, EyeIcon, LayoutGridIcon, RotateCcwIcon } from 'lucide-react';
import { pulse, traffic } from '../../data/pulse';
import { qrPanel, strategy } from '../../data/strategy';
import { storyboard } from '../../data/storyboard';
import { SERIES } from './colors';
interface PulseFrameProps {
  children: React.ReactNode;
  /** 0 before the first question, 1–5 during, 6 on the closing snapshot. */
  question: number;
  totalQuestions: number;
  footnote?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Clears this delegate's answers and returns to the opening screen. */
  onRestart?: () => void;
  /** Opens the full live results across every question. */
  onOpenData?: () => void;
  /** Opens the internal strategic brief. */
  onOpenStrategy?: () => void;
  /** Opens the printable page-by-page storyboard. */
  onOpenStoryboard?: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const CIRCLE = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-blue hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue disabled:opacity-30';
export function PulseFrame({
  children,
  question,
  totalQuestions,
  footnote,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  onRestart,
  onOpenData,
  onOpenStrategy,
  onOpenStoryboard
}: PulseFrameProps) {
  const [confirmRestart, setConfirmRestart] = useState(false);
  const inQuestions = question > 0 && question <= totalQuestions;
  const progress = Math.min(1, question / (totalQuestions + 1));
  const sourceTotal = traffic.source.reduce((sum, s) => sum + s.count, 0);
  return <div className="min-h-full w-full bg-mist" data-id="element-141" data-mp="31qmkkdnqcryd">
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col px-6 py-8 lg:px-10" data-id="element-142" data-mp="36k7kib8vvw07">
        <div className="flex items-start justify-between gap-6" data-id="element-143" data-mp="3gfeinnx7a6l3">
          <p className="text-[10px] uppercase tracking-micro text-mut" data-id="element-144" data-mp="3bg1u8yqokrge">
            {pulse.eyebrow} · congress snak
          </p>
          <p className="hidden text-[10px] uppercase tracking-micro text-mut lg:block" data-id="element-145" data-mp="37dopodno6zl7">
            Self-guided · {totalQuestions} questions
          </p>
        </div>

        <div className="flex flex-1 items-center justify-center gap-14 py-10" data-id="element-146" data-mp="2xgabkkp7ymw2">
          <div className="hidden max-w-lg flex-1 lg:block" data-id="element-147" data-mp="2xidbbwg6budz">
            <h1 className="font-display text-[54px] font-semibold leading-[0.96] tracking-tight text-navy" data-id="element-148" data-mp="2soiquqsqvbdu">
              {pulse.railTitle[0]}
              <br data-id="element-149" data-mp="2no4jlayyw5gv" />
              {pulse.railTitle[1]}
            </h1>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mut" data-id="element-150" data-mp="2jqgnw5pga8nx">{pulse.railBlurb}</p>

            <div className="mt-8 flex flex-wrap items-center gap-2.5" data-id="element-151" data-mp="2ep9g46nb59h9">
              {onOpenData && <button type="button" onClick={onOpenData} className="flex h-12 items-center gap-2.5 rounded-full bg-navy px-6 text-[13.5px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-152" data-mp="29rhm9733rdaf">
                  <BarChart3Icon className="h-4 w-4 text-mint" aria-hidden="true" data-id="element-153" data-mp="13756joue3z9g" />
                  {pulse.dataLabel}
                </button>}
              {onOpenStrategy && <button type="button" onClick={onOpenStrategy} className="flex h-12 items-center gap-2.5 rounded-full border border-line bg-paper px-6 text-[13.5px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-154" data-mp="bi20q4muugup">
                  <CompassIcon className="h-4 w-4 text-blue" aria-hidden="true" data-id="element-155" data-mp="3ny5jpm5rs0lv" />
                  {strategy.label}
                </button>}
              {onOpenStoryboard && <button type="button" onClick={onOpenStoryboard} className="flex h-12 items-center gap-2.5 rounded-full border border-line bg-paper px-6 text-[13.5px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-156" data-mp="2zvhep71hwmhg">
                  <LayoutGridIcon className="h-4 w-4 text-blue" aria-hidden="true" data-id="element-157" data-mp="3nmd09mfcylg9" />
                  {storyboard.label}
                </button>}
            </div>

            <div className="mt-10 flex max-w-md items-center gap-5 border-t border-line pt-6" data-id="element-158" data-mp="1ck72z0z5z5h">
              <img src={qrPanel.src} alt={qrPanel.alt} draggable={false} className="h-28 w-28 shrink-0 select-none object-contain" data-id="element-159" data-mp="3skeiy68vgir7" />
              <div className="min-w-0" data-id="element-160" data-mp="1y7onvl6rp3i8">
                <p className="text-[9px] uppercase tracking-micro text-mut" data-id="element-161" data-mp="dnb8gqtj0o3d">{qrPanel.eyebrow}</p>
                <p className="mt-1.5 text-[12.5px] font-medium leading-snug text-navy" data-id="element-162" data-mp="in62h38lltsn">
                  {qrPanel.caption}
                </p>
                <ul className="mt-2.5 flex flex-col gap-1" data-id="element-163" data-mp="3xhxns17kd06">
                  {traffic.source.map((slice, i) => <li key={slice.id} className="flex items-center gap-2" data-id="element-164" data-mp="8ui4441yzhrt" data-mpi={i}>
                      <span className={`h-2 w-2 shrink-0 rounded-sm ${SERIES[i % SERIES.length]}`} aria-hidden="true" data-id="element-165" data-mp="3r0wjm7fvzzsb" />
                      <span className="min-w-0 flex-1 truncate text-[11px] text-mut" data-id="element-166" data-mp="3h5urd2qbehli">
                        {slice.label}
                      </span>
                      <span className="shrink-0 text-[11px] font-semibold tabular-nums text-navy" data-id="element-167" data-mp="1fd6m6nu41k33">
                        {sourceTotal ? Math.round(slice.count / sourceTotal * 100) : 0}%
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>

            <p className="mt-6 max-w-md text-[11px] leading-relaxed text-mut" data-id="element-168" data-mp="1dvhykiqlibj2">
              {pulse.railDisclaimer}
            </p>
          </div>

          <div className="flex w-full max-w-[380px] flex-col items-center" data-id="element-169" data-mp="18yrap98jubn3">
            <p className="mb-3 text-[10px] uppercase tracking-micro text-mut" data-id="element-170" data-mp="13z4v4t590jb4">Live prototype</p>

            <div className="w-full rounded-[46px] bg-navy p-[9px] shadow-soft" data-id="element-171" data-mp="21kst3ihcr9or">
              <div className="relative flex aspect-[9/16.2] w-full flex-col overflow-hidden rounded-[38px] bg-paper" data-id="element-172" data-mp="1wnagni44mcc0">
                <div className="absolute left-1/2 top-2.5 h-4 w-20 -translate-x-1/2 rounded-full bg-navy" aria-hidden="true" data-id="element-173" data-mp="1rty5a9ogk217" />

                <div className="flex items-center justify-between gap-3 px-5 pt-9" data-id="element-174" data-mp="2kcvcv2paj638">
                  <span className="flex min-w-0 items-center gap-2" data-id="element-175" data-mp="2fkljfcoyvnw7">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-navy" aria-hidden="true" data-id="element-176" data-mp="2ntr6depc5qhs">
                      <EyeIcon className="h-3.5 w-3.5 text-mint" data-id="element-177" data-mp="2rx5y32mmzvb2" />
                    </span>
                    <span className="truncate font-display text-[12.5px] font-semibold tracking-tight text-navy" data-id="element-178" data-mp="2wwii7fm4io9y">
                      Eye Care Pulse
                    </span>
                  </span>
                  <span className="shrink-0 text-[8.5px] uppercase tracking-micro text-mut" data-id="element-179" data-mp="36qspe3fq5r4q">
                    NOK 2026
                  </span>
                </div>

                <div className="mt-3.5 flex items-center gap-2.5 px-5" data-id="element-180" data-mp="3flguntlav8tj">
                  <button type="button" onClick={onBack} disabled={!onBack} aria-label="Previous screen" className={CIRCLE} data-id="element-181" data-mp="3kk9ysvep5nh1">
                    <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-182" data-mp="3ug3hyxmx8f7s" />
                  </button>

                  <div className="h-0.5 flex-1 overflow-hidden rounded-full bg-line" data-id="element-183" data-mp="jpuz5956s97g">
                    <motion.div animate={{
                    scaleX: progress
                  }} transition={{
                    duration: 0.28,
                    ease: EASE
                  }} style={{
                    transformOrigin: 'left'
                  }} className="h-full bg-blue" data-id="element-184" data-mp="eoe4vwh7jkzu" />
                  </div>

                  <span className="shrink-0 text-[10.5px] tabular-nums text-mut" data-id="element-185" data-mp="3w32qtl6re2j5">
                    {inQuestions ? `${String(question).padStart(2, '0')}/${String(totalQuestions).padStart(2, '0')}` : question === 0 ? 'Start' : 'Done'}
                  </span>

                  {onOpenData && <button type="button" onClick={onOpenData} aria-label="See the live results across all questions" className={CIRCLE} data-id="element-186" data-mp="3dcut5iltw4ac">
                      <BarChart3Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-187" data-mp="1soscya0wiuu6" />
                    </button>}
                  {onRestart && <button type="button" onClick={() => setConfirmRestart(open => !open)} aria-label="Start over" aria-expanded={confirmRestart} className={confirmRestart ? `${CIRCLE} border-blue bg-blue/[0.08] text-blue` : CIRCLE} data-id="element-188" data-mp="1eurdfe24l3sr">
                      <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-189" data-mp="22l57tb86neqi" />
                    </button>}
                </div>

                <AnimatePresence initial={false} data-id="element-190" data-mp="r6zye2p7acns">
                  {confirmRestart && onRestart && <motion.div initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -4
                }} transition={{
                  duration: 0.16,
                  ease: EASE
                }} className="mx-5 mt-3 rounded-xl border border-line bg-mist px-3.5 py-3" data-id="element-191" data-mp="28w75k2urry7x">
                      <p className="text-[11.5px] leading-snug text-navy" data-id="element-192" data-mp="242vt9m76up8m">
                        Start over and clear your answers?
                      </p>
                      <div className="mt-2.5 flex gap-2" data-id="element-193" data-mp="1u7u4wskqlr73">
                        <button type="button" onClick={() => {
                      setConfirmRestart(false);
                      onRestart();
                    }} className="h-8 flex-1 rounded-full bg-navy text-[11.5px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-194" data-mp="1g7jc3jzva2ja">
                          Start over
                        </button>
                        <button type="button" onClick={() => setConfirmRestart(false)} className="h-8 flex-1 rounded-full border border-line bg-paper text-[11.5px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-195" data-mp="yw4dgoshu0iq">
                          Keep going
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                <div className="flex flex-1 flex-col overflow-y-auto px-5 pb-5 pt-4" data-id="element-196" data-mp="1rkjol08i7yos">{children}</div>

                {(onNext || footnote) && <div className="border-t border-line px-5 py-4" data-id="element-197" data-mp="3terd5elnvnd7">
                    {footnote && <p className="mb-3 text-[9px] leading-relaxed text-mut" data-id="element-198" data-mp="mxitzgl52z1p">{footnote}</p>}
                    {onNext && <button type="button" onClick={onNext} disabled={nextDisabled} className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-navy px-5 text-[13px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 disabled:opacity-30" data-id="element-199" data-mp="r0f4ek0izouk">
                        {nextLabel}
                        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-200" data-mp="1jmqdkj276y2z" />
                      </button>}
                  </div>}
              </div>
            </div>

            <p className="mt-3 text-[10px] uppercase tracking-micro text-mut" data-id="element-201" data-mp="1tknyawkumcqu">
              Scan to play · scroll inside the phone
            </p>
          </div>
        </div>

        <p className="text-[9.5px] uppercase tracking-micro text-mut" data-id="element-202" data-mp="39456b3ej4nvj">
          NOK 2026 · {pulse.footer} · internal concept
        </p>
      </div>
    </div>;
}